import React from "react";
import { Link } from "react-router-dom";
import { API } from "../../../../common/Constants";
import { getAPIHeaders, getAPIMultiPartHeaders } from "../../../../common/Utils";
import { all_routes } from "../../../router/all_routes";
import PreLoader from "../../../../core/common/preLoader";
import useFetch from "../../../../hooks/useFetch";
import CustomToast from "../../../../core/common/customToast";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import CopyRightInfo from "../../../../core/common/copyRightInfo";
import CommonTabMenu from "../commonTabMenu";
import SettingsMenu from "./setting-menu";
import Table from "../../../../core/common/dataTable";
import CustomModal from "../../../../core/common/customModal";
import DeleteModalV2 from "../../../../core/modals/deleteModalV2";
import { CourseCategory } from "../../../../types/dataModel";

const CourseSettings = () => {
    const routes = all_routes;
    const { isLoading, callFetch } = useFetch();
    const [toastSettings, setToastSettings] = React.useState<{
        show: boolean;
        message: string;
        heading: string;
        type: "ERROR" | "SUCCESS" | "INFO" | "";
    }>({
        show: false,
        message: "",
        heading: "",
        type: "ERROR",
    });

    const [courseCategories, setCourseCategories] = React.useState<CourseCategory[]>([]);
    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [editingCategory, setEditingCategory] = React.useState<CourseCategory | null>(null);
    const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
    const [deleteCategory, setDeleteCategory] = React.useState<CourseCategory | null>(null);
    const [formData, setFormData] = React.useState<CourseCategory>({
        id: undefined,
        name: "",
        description: "",
        metadata: {}
    });
    const [selectedThumbnailFile, setSelectedThumbnailFile] = React.useState<File | null>(null);
    const [thumbnailPreview, setThumbnailPreview] = React.useState<string | null>(null);
    const [showThumbnailModal, setShowThumbnailModal] = React.useState<boolean>(false);
    const [selectedThumbnailUrl, setSelectedThumbnailUrl] = React.useState<string | null>(null);

    const onToastClose = () => {
        setToastSettings({
            show: false,
            message: "",
            heading: "",
            type: "",
        });
    };

    const fetchCourseCategories = React.useCallback(() => {
        callFetch(API.SETTINGS_COURSE_CATEGORIES,
            'GET',
            getAPIHeaders(),
            null,
            null
        ).then((res) => {
            setCourseCategories(res || []);
        }).catch((err) => {
            setToastSettings({
                show: true,
                message: err?.response?.data || "Failed to fetch course categories.",
                heading: "Error",
                type: "ERROR",
            });
        });
    }, []);

    React.useEffect(() => {
        fetchCourseCategories();
    }, [fetchCourseCategories]);

    const handleInputChange = (field: keyof CourseCategory, value: string | Record<string, any>) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            setToastSettings({
                show: true,
                message: "Category name is required.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        if (!formData.description?.trim()) {
            setToastSettings({
                show: true,
                message: "Category description is required.",
                heading: "Validation Error",
                type: "ERROR",
            });
            return false;
        }

        return true;
    };


    const handleSaveCategory = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        try {
            // Upload thumbnail if a new one was selected
            let thumbnailFile: any | null = null;
            if (selectedThumbnailFile) {
                thumbnailFile = await uploadThumbnail(selectedThumbnailFile);
                if (!thumbnailFile) {
                    // Upload failed, don't proceed
                    return;
                }
                // Delete old thumbnail if it exists and we're editing
                if (editingCategory?.metadata?.thumbnail_file_id) {
                    await deleteThumbnail(editingCategory.metadata.thumbnail_file_id);
                }
            }

            // Prepare payload with thumbnail metadata
            const payload: CourseCategory = {
                ...formData,
                metadata: {
                    ...formData.metadata,
                }
            };

            // Include thumbnail in metadata if available
            if (thumbnailFile) {
                payload.metadata = {
                    ...payload.metadata,
                    thumbnail_file_id: thumbnailFile.id,
                    thumbnail_url: thumbnailFile.fileUrl,
                };
            } else if (editingCategory && editingCategory.metadata?.thumbnail_url && !selectedThumbnailFile) {
                // Keep existing thumbnail if no new one was selected
                payload.metadata = {
                    ...payload.metadata,
                    thumbnail_file_id: editingCategory.metadata.thumbnail_file_id,
                    thumbnail_url: editingCategory.metadata.thumbnail_url,
                };
            }

            const method = editingCategory ? 'PUT' : 'POST';

            await callFetch(API.SETTINGS_COURSE_CATEGORIES, method, getAPIHeaders(), null, payload);
            
            setToastSettings({
                show: true,
                message: editingCategory ? "Category updated successfully." : "Category created successfully.",
                heading: "Success",
                type: "SUCCESS",
            });
            setShowModal(false);
            resetForm();
            fetchCourseCategories();
        } catch (err: any) {
            setToastSettings({
                show: true,
                message: err?.response?.data || editingCategory ? "Failed to update category." : "Failed to create category.",
                heading: "Error",
                type: "ERROR",
            });
        }
    };

    const handleEditCategory = (category: CourseCategory) => {
        setEditingCategory(category);
        setFormData({
            id: category.id,
            name: category.name,
            description: category.description,
            metadata: category.metadata || {}
        });
        // Set existing thumbnail preview if available
        if (category.metadata?.thumbnail_url) {
            setThumbnailPreview(category.metadata.thumbnail_url);
        } else {
            setThumbnailPreview(null);
        }
        setSelectedThumbnailFile(null);
        setShowModal(true);
    };

    const handleDeleteCategory = (category: CourseCategory) => {
        setDeleteCategory(category);
        setShowDeleteModal(true);
    };

    const confirmDeleteCategory = async () => {
        if (!deleteCategory?.id) return;
        
        try {
            // Delete thumbnail if it exists
            if (deleteCategory.metadata?.thumbnail_file_id) {
                try {
                    await deleteThumbnail(deleteCategory.metadata.thumbnail_file_id);
                } catch (error) {
                    console.error("Error deleting thumbnail:", error);
                    // Continue with category deletion even if thumbnail deletion fails
                }
            }

            await callFetch(`${API.SETTINGS_COURSE_CATEGORIES}/${deleteCategory.id}`,
                'DELETE',
                getAPIHeaders(),
                null,
                null
            );
            
            setToastSettings({
                show: true,
                message: "Category deleted successfully.",
                heading: "Success",
                type: "SUCCESS",
            });
            fetchCourseCategories();
        } catch (err: any) {
            setToastSettings({
                show: true,
                message: err?.response?.data || "Failed to delete category.",
                heading: "Error",
                type: "ERROR",
            });
        }
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteCategory(null);
    };

    const resetForm = () => {
        setFormData({
            id: undefined,
            name: "",
            description: "",
            metadata: {}
        });
        setEditingCategory(null);
        setSelectedThumbnailFile(null);
        setThumbnailPreview(null);
    };

    // Upload thumbnail using API.ADD_FILES
    const uploadThumbnail = async (file: File): Promise<any | null> => {
        try {
            const formData = new FormData();
            formData.append("files", file);
            formData.append("isPublic", "true");

            const response = await callFetch(
                API.ADD_FILES,
                "POST",
                getAPIMultiPartHeaders(),
                null,
                formData
            );

            // API.ADD_FILES returns an array, get the first file's id and fileUrl
            if (Array.isArray(response) && response.length > 0 && response[0].id) {
                return response[0] as any;
            }
            return null;
        } catch (error) {
            console.error("Error uploading thumbnail:", error);
            setToastSettings({
                show: true,
                message: "Failed to upload thumbnail. Please try again.",
                heading: "Error",
                type: "ERROR",
            });
            return null;
        }
    };

    // Delete thumbnail using API.FILES
    const deleteThumbnail = async (thumbnailFileId: number): Promise<void> => {
        try {
            await callFetch(
                API.FILES.replace(":fileId", thumbnailFileId.toString()),
                "DELETE",
                getAPIHeaders(),
                null,
                null
            );
        } catch (error) {
            console.error("Error deleting thumbnail:", error);
            // Don't throw - continue with category update even if thumbnail deletion fails
        }
    };

    const handleThumbnailFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith("image/")) {
                setToastSettings({
                    show: true,
                    message: "Please select an image file for thumbnail",
                    heading: "Invalid File Type",
                    type: "ERROR",
                });
                event.target.value = "";
                return;
            }

            setSelectedThumbnailFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setThumbnailPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
        event.target.value = "";
    };

    const handleRemoveThumbnail = async () => {
        // If there's an existing thumbnail in the category being edited, delete it
        if (editingCategory?.metadata?.thumbnail_file_id) {
            try {
                await deleteThumbnail(editingCategory.metadata.thumbnail_file_id);
            } catch (error) {
                console.error("Error deleting existing thumbnail:", error);
            }
        }
        setSelectedThumbnailFile(null);
        setThumbnailPreview(null);
    };

    const handleThumbnailClick = (thumbnailUrl: string) => {
        setSelectedThumbnailUrl(thumbnailUrl);
        setShowThumbnailModal(true);
    };

    const handleCloseThumbnailModal = () => {
        setShowThumbnailModal(false);
        setSelectedThumbnailUrl(null);
    };

    const openAddModal = () => {
        resetForm();
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        resetForm();
    };

    const tableColumns = [
        {
            title: 'Thumbnail',
            key: 'thumbnail',
            width: 120,
            render: (text: any, record: CourseCategory) => {
                const thumbnailUrl = record.metadata?.thumbnail_url;
                if (thumbnailUrl) {
                    return (
                        <img
                            src={thumbnailUrl}
                            alt={`${record.name} thumbnail`}
                            className="rounded"
                            style={{ width: "80px", height: "80px", objectFit: "cover", cursor: "pointer" }}
                            onClick={() => handleThumbnailClick(thumbnailUrl)}
                        />
                    );
                }
                return <span className="text-muted ">No thumbnail</span>;
            },
        },
        {
            title: 'Category Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: CourseCategory) => (
                <div>
                    <h6 className="mb-0">{text}</h6>
                    <small className="text-muted">{record.description}</small>
                </div>
            ),
            defaultSortOrder: 'ascend',
            sorter: (a: any, b: any) => a.name?.localeCompare(b.name),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text: any, record: CourseCategory) => (
                <div className="d-flex align-items-center">
                    <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEditCategory(record)}
                        title="Edit Category"
                    >
                        <i className="ti ti-edit" />
                    </button>
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteCategory(record)}
                        title="Delete Category"
                    >
                        <i className="ti ti-trash" />
                    </button>
                </div>
            )
        }
    ];

    const modalBody = (
        <form onSubmit={handleSaveCategory}>
            <div className="mb-3">
                <label className="form-label">Category Name <span className="text-danger">*</span></label>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Enter category name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                />
            </div>

            <div className="mb-3">
                <label className="form-label">Description <span className="text-danger">*</span></label>
                <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter category description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    required
                />
            </div>

            <div className="mb-3">
                <label className="form-label">Thumbnail</label>
                {thumbnailPreview || editingCategory?.metadata?.thumbnail_url ? (
                    <div className="d-flex align-items-center gap-3 mb-2">
                        <div className="flex-shrink-0">
                            <img
                                src={thumbnailPreview || editingCategory?.metadata?.thumbnail_url || ""}
                                alt="Category thumbnail"
                                className="rounded"
                                style={{ width: "128px", height: "128px", objectFit: "cover", cursor: "pointer" }}
                                onClick={() => handleThumbnailClick(thumbnailPreview || editingCategory?.metadata?.thumbnail_url || "")}
                            />
                        </div>
                        {/* <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={handleRemoveThumbnail}
                        >
                            <i className="ti ti-trash" /> Remove
                        </button> */}
                    </div>
                ) : (
                    <div className="mb-2">
                        <div className="fs-12 text-muted mb-2">No thumbnail available. Select an image file to add one.</div>
                    </div>
                )}
                <input
                    type="file"
                    className="form-control form-control-sm"
                    accept="image/*"
                    onChange={handleThumbnailFileChange}
                />
                <div className="form-text">Select an image file (JPG, PNG, etc.) for thumbnail. Thumbnail should be in 1:1 ratio.</div>
            </div>

            <div className="d-flex justify-content-end gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeModal}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                >
                    {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
            </div>
        </form>
    );

    return (
        <div>
            <CustomToast
                show={toastSettings?.show}
                message={toastSettings?.message}
                heading={toastSettings?.heading}
                type={toastSettings?.type || "INFO"}
                onClose={onToastClose} 
            />
            <PreLoader showLoader={isLoading} />
            
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Application Settings</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Administration</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Settings
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="head-icons ms-2">
                            <CollapseHeader />
                        </div>
                    </div>
                    {/* /Breadcrumb */}
                    
                    <CommonTabMenu activeTab="WebsiteSettings" />
                    
                    <div className="row">
                        <div className="col-xl-3 theiaStickySidebar">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex flex-column list-group settings-list">
                                        <SettingsMenu activeTab="CourseSetting" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-xl-9">
                            <div className="card">
                                <div className="card-body">
                                    <div className="border-bottom mb-3 pb-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <div>
                                                    <h4>Course Settings</h4>
                                                    <p className="text-muted mb-0">Manage course categories and default settings</p>
                                                </div>
                                            </div>
                                            <button
                                                className="btn btn-primary"
                                                onClick={openAddModal}
                                            >
                                                <i className="ti ti-plus me-2" />
                                                Add Category
                                            </button>
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <Table 
                                            dataSource={courseCategories} 
                                            columns={tableColumns} 
                                            rowKey="id"
                                            Selection={false}
                                            showSearch={true}
                                            showTableTopData={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
                    <CopyRightInfo />
                </div>
            </div>
            {/* /Page Wrapper */}

            {/* Modal */}
            <CustomModal
                show={showModal}
                title={editingCategory ? "Edit Course Category" : "Add Course Category"}
                onClose={closeModal}
                bodyContent={modalBody}
             
            />

            {/* Delete Confirmation Modal */}
            <DeleteModalV2
                show={showDeleteModal}
                onClose={closeDeleteModal}
                heading="Delete Course Category"
                message={deleteCategory ? `Are you sure you want to delete "${deleteCategory.name}"? This action cannot be undone.` : ""}
                btnText="Yes, Delete"
                onDelete={confirmDeleteCategory}
            />

            {/* Thumbnail Preview Modal */}
            <CustomModal
                zindex={99999}
                show={showThumbnailModal}
                onClose={handleCloseThumbnailModal}
                title="Thumbnail Preview"
                size="lg"
                bodyContent={
                    selectedThumbnailUrl && (
                        <div className="text-center">
                            <img
                                src={selectedThumbnailUrl}
                                alt="Category thumbnail preview"
                                className="img-fluid rounded"
                                style={{ width: "100%", maxWidth: "500px", aspectRatio: "1/1", objectFit: "contain" }}
                            />
                        </div>
                    )
                }
                footerContent={
                    <button type="button" className="btn btn-secondary" onClick={handleCloseThumbnailModal}>
                        Close
                    </button>
                }
            />
        </div>
    );
};

export default CourseSettings;
